import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { renderJobDescription } from '../../utils/renderDescription';
import LanguageToggle from '../../components/LanguageToggle';
import ZoomableImg from '../../components/ZoomableImg';
import PortfolioGallery from '../../components/PortfolioGallery';
import { joinParts } from '../../utils/joinParts';
import LineIcon from '../../components/LineIcon';

// Public page (signed-out visitors included). anon holds column-level SELECT on exactly these
// employers columns -- email/phone/full_name/plan_tier/etc. are hard-blocked -- so the query
// names them explicitly: select('*') would fail outright for a signed-out visitor.
const PUBLIC_EMPLOYER_COLUMNS = 'id, company_name, logo_url, about, website, industry, city, state, year_founded, company_size, union_status, typical_benefits, other_benefits, is_demo';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const YELLOW    = '#FACC15';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function CompanyAvatar({ logoUrl, initials, size = 80 }) {
  if (logoUrl) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: NAVY_MID }}>
        <ZoomableImg src={logoUrl} alt="Company logo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      backgroundColor: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontWeight: 700, fontSize: size * 0.38, color: NAVY, fontFamily: FONT_BODY }}>
        {initials}
      </span>
    </div>
  );
}

// company_size is stored as the English option value from the employer forms (older rows use a
// bare "1-10"); map known values to the translated tier label, anything else shows as stored.
const COMPANY_SIZE_TIERS = { '1-10': 'tier1', '11-50': 'tier2', '51-200': 'tier3', '200+': 'tier4' };
function companySizeLabel(size, t) {
  const tier = COMPANY_SIZE_TIERS[size.replace(/–/g, '-').replace(/\s*employees$/i, '').trim()];
  return tier ? t(`employer.companyForm.companySizeOptions.${tier}`) : size;
}

const badge = {
  fontSize: '12px', fontWeight: 600, color: WHITE,
  backgroundColor: 'rgba(255,255,255,0.09)',
  borderRadius: '20px', padding: '5px 12px',
};

export default function CompanyProfile() {
  const navigate = useNavigate();
  const { employerId } = useParams();
  const { t } = useTranslation();

  const [employer, setEmployer]   = useState(null);
  const [jobs, setJobs]           = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [workerId, setWorkerId]   = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: ${FONT_BODY}; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      setWorkerId(uid);

      const todayISO = new Date().toISOString().slice(0, 10);
      const [empRes, jobsRes, portfolioRes] = await Promise.all([
        supabase.from('employers').select(PUBLIC_EMPLOYER_COLUMNS).eq('id', employerId).maybeSingle(),
        supabase.from('jobs').select('*').eq('employer_id', employerId).eq('status', 'active').or(`expires_at.is.null,expires_at.gte.${todayISO}`).order('created_at', { ascending: false }),
        supabase.from('employer_portfolio').select('*').eq('employer_id', employerId).order('created_at', { ascending: false }),
      ]);

      setEmployer(empRes.data);
      setPortfolio(portfolioRes.data || []);
      const jobList = jobsRes.data || [];
      setJobs(jobList);

      if (uid) {
        const jobIds = jobList.map(j => j.id);
        const [appsRes, savedRes] = await Promise.all([
          supabase.from('applications').select('job_id').eq('worker_id', uid),
          jobIds.length > 0
            ? supabase.from('saved_jobs').select('job_id').eq('worker_id', uid).in('job_id', jobIds)
            : Promise.resolve({ data: [] }),
        ]);
        setAppliedJobs(appsRes.data?.map(a => a.job_id) || []);
        setSavedJobIds(new Set((savedRes.data || []).map(s => s.job_id)));
      }

      setLoading(false);
    };
    load();
  }, [employerId]); // eslint-disable-line

  const handleInterested = async (job, e) => {
    e.stopPropagation();
    // Signed-out visitor: same move as the public union page's Apply -- start worker signup.
    if (!workerId) { navigate('/worker/signup'); return; }
    if (appliedJobs.includes(job.id)) return;
    await supabase.from('applications').insert({
      worker_id:   workerId,
      employer_id: job.employer_id,
      job_id:      job.id,
      status:      'applied',
    });
    setAppliedJobs(prev => [...prev, job.id]);
    supabase.from('notifications').insert({
      user_id:   job.employer_id,
      user_type: 'employer',
      type:      'new_application',
      message:   `A worker is interested in your ${job.job_title} position`,
    }).then(() => {});
  };

  const handleToggleSave = async (job, e) => {
    e.stopPropagation();
    if (!workerId) return;
    if (savedJobIds.has(job.id)) {
      await supabase.from('saved_jobs').delete().eq('worker_id', workerId).eq('job_id', job.id);
      setSavedJobIds(prev => { const n = new Set(prev); n.delete(job.id); return n; });
    } else {
      await supabase.from('saved_jobs').insert({ worker_id: workerId, job_id: job.id });
      setSavedJobIds(prev => new Set([...prev, job.id]));
    }
  };

  if (loading) {
    return (
      <div style={{ background: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: FONT_BODY, fontSize: '14px' }}>{t('worker.companyProfile.loading')}</span>
      </div>
    );
  }

  if (!employer) {
    return (
      <div style={{ background: NAVY, minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontFamily: FONT_BODY, fontSize: '14px' }}>{t('worker.companyProfile.companyNotFound')}</span>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: AMBER, fontFamily: FONT_BODY, fontSize: '14px', cursor: 'pointer' }}>{t('worker.companyProfile.goBack')}</button>
      </div>
    );
  }

  const benefits = Array.isArray(employer.typical_benefits) ? employer.typical_benefits : [];

  return (
    <div style={{ background: NAVY, minHeight: '100dvh', fontFamily: FONT_BODY }}>

      {/* Nav bar */}
      <div style={{ backgroundColor: NAVY_MID, borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(workerId ? '/worker/dashboard' : '/')}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
        >
          {t('worker.companyProfile.backButton')}
        </button>
        <div style={{ marginLeft: 'auto' }}>
          <LanguageToggle dark />
        </div>
      </div>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 20px 80px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
          <CompanyAvatar logoUrl={employer.logo_url} initials={employer.company_name?.[0] || '?'} size={80} />
          <div>
            <h1 style={{ fontFamily: FONT_HEAD, fontSize: 'clamp(1.8rem, 5vw, 2.4rem)', color: WHITE, letterSpacing: '0.04em', lineHeight: 1.05 }}>
              {employer.company_name}
            </h1>
            {(employer.city || employer.state) && (
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.48)', marginTop: '5px' }}>
                {[employer.city, employer.state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>

        {/* Meta badges */}
        {(employer.industry || employer.company_size || employer.year_founded || employer.union_status) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
            {employer.industry    && <span style={badge}>{employer.industry}</span>}
            {employer.company_size && <span style={badge} data-company-size>{companySizeLabel(employer.company_size, t)}</span>}
            {employer.year_founded && <span style={badge}>{t('worker.companyProfile.estPrefix')} {employer.year_founded}</span>}
            {employer.union_status && (
              <span style={{ ...badge, color: AMBER, backgroundColor: 'rgba(245,166,35,0.12)', border: '1px solid rgba(245,166,35,0.25)' }}>
                {employer.union_status}
              </span>
            )}
          </div>
        )}

        {/* About */}
        {employer.about && (
          <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '10px' }}>
              {t('worker.companyProfile.about')}
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.78)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {employer.about}
            </p>
          </div>
        )}

        {/* Benefits */}
        {(benefits.length > 0 || employer.other_benefits) && (
          <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('worker.companyProfile.typicalBenefits')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {benefits.map((b, i) => (
                <span key={i} style={{ fontSize: '12px', fontWeight: 600, color: NAVY, backgroundColor: AMBER, borderRadius: '20px', padding: '5px 12px' }}>
                  {b}
                </span>
              ))}
            </div>
            {employer.other_benefits && (
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '10px', lineHeight: 1.5 }}>
                {employer.other_benefits}
              </p>
            )}
          </div>
        )}

        {/* Videos -- employer_portfolio, publicly readable */}
        {portfolio.length > 0 && (
          <div data-public-portfolio style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('portfolio.publicHeading')}
            </p>
            <PortfolioGallery items={portfolio} variant="dark" />
          </div>
        )}

        {/* Website */}
        {employer.website && (
          <div style={{ marginBottom: '28px' }}>
            <a
              href={employer.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                backgroundColor: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.28)',
                color: AMBER, borderRadius: '8px', padding: '10px 18px',
                fontSize: '14px', fontWeight: 600, textDecoration: 'none', fontFamily: FONT_BODY,
              }}
            >
              {t('worker.companyProfile.visitWebsite')}
            </a>
          </div>
        )}

        {/* Job listings */}
        <div>
          <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '14px' }}>
            {t('worker.companyProfile.openPositions', { count: jobs.length })}
          </p>

          {jobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>
              {t('worker.companyProfile.noOpenPositions')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {jobs.map(job => (
                <div
                  key={job.id}
                  style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px', cursor: 'pointer' }}
                  onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: WHITE }}>{job.job_title}</span>
                        {job.urgently_hiring && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: '#E24B4A', borderRadius: '999px', padding: '2px 8px' }}><LineIcon name="flame" size={16} color={YELLOW} />{t('worker.dashboard.jobs.urgentlyHiring')}</span>
                        )}
                        {job.willing_to_train && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: AMBER, backgroundColor: NAVY, border: `1px solid ${AMBER}`, borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.willingToTrain')}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>{joinParts([job.city, job.state], ', ')}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', color: AMBER, fontWeight: 500 }}>${job.salary_min}–${job.salary_max}/hr</span>
                        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{joinParts([job.job_type, job.union_status])}</span>
                        {job.trade_category && (
                          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', padding: '2px 6px' }}>
                            {job.trade_category}
                          </span>
                        )}
                        {job.shift && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{job.shift}</span>
                        )}
                        {job.per_diem && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.perDiem')}</span>
                        )}
                        {job.sign_on_bonus_amount && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.signOnBonus')}</span>
                        )}
                        {job.travel_required && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.travelRequired')}</span>
                        )}
                        {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('worker.dashboard.jobs.benefitsCount', { count: job.benefits.length })}</span>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginLeft: '10px' }}>
                      {expandedJob === job.id ? '▲' : '▼'}
                    </span>
                  </div>

                  {expandedJob === job.id && (
                    <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '16px' }}>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.trade')}</div>
                      <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.trade_category}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.jobTypeUnionStatus')}</div>
                      <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{joinParts([job.job_type, job.union_status])}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.experienceRequired')}</div>
                      <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.min_years_experience}</div>
                      {job.certifications_required && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.certsRequired')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.certifications_required}</div>
                        </>
                      )}
                      {Array.isArray(job.certifications_preferred) && job.certifications_preferred.length > 0 && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.certsPreferred')}</div>
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginBottom: '12px' }}>{job.certifications_preferred.join(', ')}</div>
                        </>
                      )}
                      {job.per_diem && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.perDiem')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.per_diem_amount || t('common.yes')}</div>
                        </>
                      )}
                      {job.sign_on_bonus_amount && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.signOnBonus')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>${job.sign_on_bonus_amount}</div>
                        </>
                      )}
                      {job.travel_required && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.jobs.travelLabel')}</div>
                          <div style={{ fontSize: '13px', color: WHITE, marginBottom: '12px' }}>{job.travel_details || t('worker.dashboard.jobs.requiredFallback')}</div>
                        </>
                      )}
                      {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                        <>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.dashboard.applications.benefitsLabel')}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                            {job.benefits.map(b => (
                              <span key={b} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                            ))}
                          </div>
                        </>
                      )}
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.jobDescription')}</div>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', lineHeight: '1.6' }}>{renderJobDescription(job.job_description)}</div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={e => handleInterested(job, e)}
                          style={{
                            background: appliedJobs.includes(job.id) ? GREEN : AMBER,
                            border: 'none',
                            color: appliedJobs.includes(job.id) ? WHITE : NAVY,
                            fontSize: '11px', fontWeight: 500,
                            padding: '8px 16px', borderRadius: '6px', cursor: 'pointer',
                          }}
                        >
                          {appliedJobs.includes(job.id) ? t('worker.companyProfile.interestedDone') : t('worker.companyProfile.interested')}
                        </button>
                        {workerId && (
                        <button
                          onClick={e => handleToggleSave(job, e)}
                          title={savedJobIds.has(job.id) ? t('worker.companyProfile.unsaveJob') : t('worker.companyProfile.saveJob')}
                          aria-label={savedJobIds.has(job.id) ? t('worker.companyProfile.unsaveJob') : t('worker.companyProfile.saveJob')}
                          aria-pressed={savedJobIds.has(job.id)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', flexShrink: 0,
                            background: savedJobIds.has(job.id) ? 'rgba(245,166,35,0.12)' : 'transparent',
                            border: `1px solid ${savedJobIds.has(job.id) ? AMBER : 'rgba(255,255,255,0.2)'}`,
                            color: savedJobIds.has(job.id) ? AMBER : 'rgba(255,255,255,0.6)',
                            padding: 0, borderRadius: '8px', cursor: 'pointer',
                          }}
                        >
                          <LineIcon name="bookmark" filled={savedJobIds.has(job.id)} />
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
